import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { Portal } from '@xing-com/portal';

import { DialogPrimitive } from './dialog.primitive';
import * as Styled from './dialog.styles';
import type { DialogProps } from './dialog.types';
const {
  dialogDuration,
  dialogTransitionName,
  dimmerDuration,
  dimmerTransitionName,
} = Styled;

export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      cancelLabel,
      confirmLabel,
      focusTrapProps,
      headline,
      isDestructive,
      keepDialogInDom,
      loading,
      onCancel,
      onConfirm,
      screenReaderDescription,
      show = false,
      text,
      variant,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const dimmerRef = React.useRef<HTMLDivElement>(null);

    // Prevent scrolling when dialog is open
    React.useEffect(() => {
      if (show) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }

      return undefined;
    }, [show]);

    // Keyboard handling
    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent): void => {
        if (show && onCancel && event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      };

      document.addEventListener('keydown', handleKey);

      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [onCancel, onConfirm, show]);

    const showCancelAction = Boolean(cancelLabel && onCancel);

    const { focusTrapOptions: userFocusTrapOptions, ...otherFocusTrapProps } =
      focusTrapProps || {};
    const mergedFocusTrapOptions = {
      escapeDeactivates: Boolean(onCancel),
      returnFocusOnDeactivate: true,
      ...(userFocusTrapOptions || {}),
    };

    const DialogTransition = (
      <CSSTransition
        appear={show}
        classNames={dialogTransitionName}
        in={show}
        key="dialog"
        mountOnEnter={!keepDialogInDom}
        timeout={parseInt(dialogDuration)}
        unmountOnExit={!keepDialogInDom}
        nodeRef={containerRef}
      >
        <Styled.Container {...props} ref={containerRef}>
          <FocusTrap
            active={show}
            {...otherFocusTrapProps}
            focusTrapOptions={mergedFocusTrapOptions}
          >
            <DialogPrimitive
              cancelLabel={cancelLabel}
              confirmLabel={confirmLabel}
              headline={headline}
              isDestructive={isDestructive}
              loading={loading}
              onCancel={onCancel}
              onConfirm={onConfirm}
              ref={forwardedRef}
              screenReaderDescription={screenReaderDescription}
              showCancelAction={showCancelAction}
              text={text}
              variant={variant}
            />
          </FocusTrap>
        </Styled.Container>
      </CSSTransition>
    );

    const DimmerTransition = (
      <CSSTransition
        appear={show}
        classNames={dimmerTransitionName}
        in={show}
        key="dimmer"
        mountOnEnter
        timeout={parseInt(dimmerDuration)}
        unmountOnExit
        nodeRef={dimmerRef}
      >
        <Styled.Dimmer
          data-testid="dialog-dimmer"
          data-qa="dialog-dimmer"
          ref={dimmerRef}
        />
      </CSSTransition>
    );

    return (
      <>
        <Portal>{DialogTransition}</Portal>
        {DimmerTransition}
      </>
    );
  }
);

Dialog.displayName = 'Dialog';
